import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Flex, Spinner, Text, View } from "@instructure/ui";
import { useNotification } from "../../../components/CustomHooks/NotificationProvider";
import { formatToReadableTime } from "../../../utils/utils";
import { printAgenda } from "./printAgendaUtils";

/**
 * PublishedAgendas — Renders the Published Agendas history section.
 * Owns print logic internally (fetches file content + opens print window).
 */
const PublishedAgendas = ({
  publishedAgendas,
  loadingPublished,
  planId,
  fileService,
}) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const [printingFile, setPrintingFile] = useState(null);

  const handlePrint = async (entry) => {
    setPrintingFile(entry.file_name);
    try {
      const response = await fileService.getFileContent(
        entry.file_name,
        "agenda",
        planId
      );
      const rawContent = response?.content || response;
      const parsed =
        typeof rawContent === "string" ? JSON.parse(rawContent) : rawContent;
      printAgenda(parsed, { showPublishedFooter: true });
    } catch {
      showNotification(
        t("assessmentPlanningDashboard.meetingAgenda.fetchError"),
        "error"
      );
    } finally {
      setPrintingFile(null);
    }
  };

  if (loadingPublished) {
    return (
      <Flex justifyItems="center" padding="medium">
        <Spinner
          renderTitle={t(
            "assessmentPlanningDashboard.meetingAgenda.loadingPublished"
          )}
          size="small"
        />
      </Flex>
    );
  }

  if (publishedAgendas.length === 0) {
    return null;
  }

  return (
    <View as="div" margin="large 0 0 0" className="no-print">
      <View as="div" margin="0 0 small 0">
        <Text
          weight="bold"
          size="small"
          transform="uppercase"
          letterSpacing="expanded"
        >
          {t(
            "assessmentPlanningDashboard.meetingAgenda.publishedAgendaHistory"
          )}
        </Text>
      </View>

      {publishedAgendas.map((entry) => {
        const fileName = entry.file_name || "";
        const agendaName = entry.agenda_name || "";
        const meetingDate = entry.agenda_meeting_date || "";
        const itemCount = entry.agenda_item_count || 0;
        const timestamp = entry.last_modified
          ? formatToReadableTime(new Date(entry.last_modified))
          : "";
        const isPrinting = printingFile === fileName;

        return (
          <View
            key={fileName}
            as="div"
            padding="small"
            margin="0 0 x-small 0"
            borderWidth="small"
            borderRadius="medium"
          >
            <Flex
              justifyItems="space-between"
              alignItems="center"
              wrap="wrap"
              gap="small"
            >
              <Flex.Item shouldGrow shouldShrink>
                <Flex alignItems="center" gap="small" wrap="wrap">
                  {agendaName && (
                    <Flex.Item>
                      <Text weight="bold">{agendaName}</Text>
                    </Flex.Item>
                  )}
                  {meetingDate && (
                    <Flex.Item>
                      <Text size="small" color="secondary">
                        {meetingDate}
                      </Text>
                    </Flex.Item>
                  )}
                  <Flex.Item>
                    <Text size="small" color="secondary">
                      {t(
                        "assessmentPlanningDashboard.meetingAgenda.itemCount",
                        { count: itemCount }
                      )}
                    </Text>
                  </Flex.Item>
                  {timestamp && (
                    <Flex.Item>
                      <Text size="x-small" color="secondary">
                        {t(
                          "assessmentPlanningDashboard.meetingAgenda.publishedOn",
                          { date: timestamp }
                        )}
                      </Text>
                    </Flex.Item>
                  )}
                </Flex>
              </Flex.Item>
              <Flex.Item>
                <Button
                  size="small"
                  onClick={() => handlePrint(entry)}
                  interaction={isPrinting ? "disabled" : "enabled"}
                  aria-label={t(
                    "assessmentPlanningDashboard.meetingAgenda.printVersion",
                    { name: agendaName || fileName }
                  )}
                >
                  {isPrinting ? (
                    <Spinner
                      size="x-small"
                      renderTitle={t(
                        "assessmentPlanningDashboard.meetingAgenda.print"
                      )}
                    />
                  ) : (
                    t("assessmentPlanningDashboard.meetingAgenda.print")
                  )}
                </Button>
              </Flex.Item>
            </Flex>
          </View>
        );
      })}
    </View>
  );
};

export default PublishedAgendas;
